type EventHandler<T = never> = (payload: T) => void;

export class EventEmitter<EventMap extends { [K in keyof EventMap]: unknown }> {
  private handlers = new Map<keyof EventMap, EventHandler<any>[]>();
  // Last payload emitted per event, so late subscribers (e.g. a component that mounts after the
  // one broadcast already fired) can read the current value instead of missing it entirely.
  private lastPayloads = new Map<keyof EventMap, unknown>();

  on<EventName extends keyof EventMap>(
    event: EventName,
    callback: EventHandler<EventMap[EventName]>,
  ) {
    const existing = this.handlers.get(event) || [];
    this.handlers.set(event, [...existing, callback]);
  }

  off<EventName extends keyof EventMap>(
    event: EventName,
    callback: EventHandler<EventMap[EventName]>,
  ) {
    const handlers = this.handlers.get(event) || [];
    this.handlers.set(
      event,
      handlers.filter((handler) => handler !== callback),
    );
  }

  emit<EventName extends keyof EventMap>(event: EventName, payload?: EventMap[EventName]) {
    this.lastPayloads.set(event, payload);
    const handlers = this.handlers.get(event) || [];
    handlers.forEach((handler) => handler(payload));
  }

  /** Returns the most recent payload emitted for this event, if any. */
  getLast<EventName extends keyof EventMap>(event: EventName): EventMap[EventName] | undefined {
    return this.lastPayloads.get(event) as EventMap[EventName] | undefined;
  }
}
